import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { useBrand } from '../../app/config/brand';
import { useIsMobile } from '../hooks/useIsMobile';
import { Sidebar } from './components/Sidebar';
import { NewHeader } from './components/NewHeader';
import { Footer } from './components/Footer';
import { ChatBubble } from './components/ChatBubble';
import { ServiceSelectionModal } from './components/ServiceSelectionModal';
import { withFreshStartSignal } from '../../features/efiling/lib/resolve-entry-intent';
import { MAIN_CONTENT_ID } from '../components/SkipLink';

interface MainLayoutProps {
  children: React.ReactNode;
  onLogout: () => void;
  user?: { name?: string; email?: string; picture?: string };
}

export const MainLayout: React.FC<MainLayoutProps> = ({ children, onLogout, user }) => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const isMobile = useIsMobile();
  const brand = useBrand();
  const navigate = useNavigate();
  const location = useLocation();

  // Derive current page from URL path
  const currentPage = location.pathname.replace('/', '') || 'dashboard';

  // Navigation handler that uses React Router
  const handleNavigate = (page: string) => {
    navigate(`/${page}`);
  };

  // Derive effective mobile menu state — auto-close when viewport widens to desktop
  const effectiveMobileMenuOpen = isMobile && isMobileMenuOpen;

  return (
    <div className="min-h-screen" style={{ backgroundColor: brand.colors.bgLight }}>
      <Sidebar
        currentPage={currentPage}
        onNavigate={handleNavigate}
        isCollapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
        onPlaceOrder={() => setShowServiceModal(true)}
        isMobileOpen={effectiveMobileMenuOpen}
        onMobileToggle={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
      />

      <div
        className="transition-all duration-300 min-h-screen flex flex-col w-full lg:w-auto"
        style={{ marginLeft: isMobile ? 0 : (sidebarCollapsed ? '80px' : '256px') }}
      >
        <NewHeader onLogout={onLogout} user={user} />
        <main id={MAIN_CONTENT_ID} tabIndex={-1} className="flex-1 p-4 sm:p-6 overflow-auto">
          {children}
        </main>
        <Footer />
      </div>

      <ServiceSelectionModal
        isOpen={showServiceModal}
        onClose={() => setShowServiceModal(false)}
        onSelectService={(serviceId) => {
          if (serviceId === 'e-filing') {
            // Fresh-start launch site: carry the fresh-start signal (and no
            // ?orderId=) so the wizard resolves on entry as a fresh start (R6).
            navigate(withFreshStartSignal('/eFiling'));
          } else {
            navigate(`/orders/new/${serviceId}`);
          }
        }}
      />

      <ChatBubble />
    </div>
  );
};
